"use client"

/**
 * components/hero-section.tsx — Aperture-Light landing hero.
 *
 * Slimmed down from the original 2,820-line file (which carried a
 * pre-`/try` upload pipeline behind `display:none`). The upload UI now
 * lives at /try; this component is purely presentational:
 *
 *   - Product availability pill (no volume/social-proof counter)
 *   - Editorial H1 + subhead with word-rise reveal
 *   - HeroLiveValidator (one-email demo)
 *   - Two CTAs to /try and #how-it-works
 *   - Top-right HeroApiSnippet (xl+ only)
 *
 * Animations honour `prefers-reduced-motion` via the global guard at
 * the bottom — orbs, grid drift, headline reveal, accent-shimmer and
 * CTA shimmer are all neutralised when the user opts out.
 */

import HeroApiSnippet     from "@/components/hero/HeroApiSnippet"
import HeroLiveValidator  from "@/components/hero/HeroLiveValidator"

export default function HeroSection() {
  return (
    <section
      id="upload"
      className="relative overflow-hidden w-full"
      style={{ background: "#FAF7EE" }}
    >
      {/* Aperture-Light keyframes scoped to the hero. Inline so we
          don't pollute globals.css for a single section. The global
          @media (prefers-reduced-motion: reduce) at the bottom of
          this <style> tag neutralises every animation listed here. */}
      <style>{`
        @keyframes hero-ring-pulse {
          0%   { transform: scale(0.95); opacity: 0.6 }
          70%  { transform: scale(1.4);  opacity: 0   }
          100% { transform: scale(1.4);  opacity: 0   }
        }
        @keyframes hero-float-a {
          0%,100% { transform: translate(0,0) scale(1) }
          50%     { transform: translate(30px,-20px) scale(1.06) }
        }
        @keyframes hero-float-b {
          0%,100% { transform: translate(0,0) scale(1) }
          50%     { transform: translate(-40px,30px) scale(1.10) }
        }
        @keyframes hero-grid-drift {
          from { background-position: 0 0 }
          to   { background-position: 44px 44px }
        }
        @keyframes hero-rise {
          from { opacity: 0; transform: translateY(20px) }
          to   { opacity: 1; transform: translateY(0) }
        }
        @keyframes hero-word-rise {
          from { opacity: 0; transform: translateY(28px); filter: blur(8px) }
          to   { opacity: 1; transform: translateY(0);    filter: blur(0)   }
        }
        @keyframes hero-shimmer-x {
          0%       { transform: translateX(-160%) skewX(-12deg); opacity: 0 }
          15%      { opacity: 1 }
          50%      { transform: translateX(160%)  skewX(-12deg); opacity: 1 }
          51%,100% { opacity: 0 }
        }
        @keyframes hero-accent-shimmer {
          0%,80%,100% { background-position: -200% 0 }
          40%         { background-position: 200% 0 }
        }
        @media (prefers-reduced-motion: reduce) {
          #upload *, #upload *::before, #upload *::after {
            animation: none !important;
            transition: none !important;
          }
        }

        /* Smallest realistic viewport in the wild (≈320 CSS px — iPhone
           5/SE 1st gen, old Androids, Galaxy Fold cover). The headline
           had two pieces forced to nowrap: "Email deliverability" and
           "for serious senders." — both render around 280-310px at our
           extrabold/italic weight, which overflows a 272px container
           (320 viewport - 48px side padding). Below 360px we drop the
           nowrap on those fragments so they wrap naturally, and pull
           the side padding from 24 → 16 so words have more room. */
        @media (max-width: 360px) {
          .hero-container        { padding-left: 16px !important; padding-right: 16px !important; }
          .hero-h1-nowrap        { white-space: normal !important; }
        }
      `}</style>

      {/* Subtle ink dot grid — paper-stock feel on cream. */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle, rgba(15,13,10,0.045) 1px, transparent 1px)",
          backgroundSize: "32px 32px",
        }}
      />

      {/* Two emerald halos drifting in opposite directions. Ambient
          motion without distracting copy. */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div
          className="absolute -top-32 right-0 w-[800px] h-[560px] rounded-full"
          style={{
            background: "rgba(15,81,50,0.10)",
            filter: "blur(140px)",
            animation: "hero-float-a 14s ease-in-out infinite",
          }}
        />
        <div
          className="absolute top-40 left-0 w-[420px] h-[320px] rounded-full"
          style={{
            background: "rgba(15,81,50,0.06)",
            filter: "blur(110px)",
            animation: "hero-float-b 18s ease-in-out infinite",
          }}
        />
      </div>

      {/* Drifting hairline grid — barely perceptible, masked at the
          edges so it dissolves into the cream surface. */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "linear-gradient(rgba(10,10,10,0.045) 1px, transparent 1px), linear-gradient(90deg, rgba(10,10,10,0.045) 1px, transparent 1px)",
          backgroundSize: "44px 44px",
          maskImage:
            "radial-gradient(900px 600px at 50% 30%, black, transparent 80%)",
          WebkitMaskImage:
            "radial-gradient(900px 600px at 50% 30%, black, transparent 80%)",
          animation: "hero-grid-drift 30s linear infinite",
        }}
      />

      {/* Top-right API snippet — xl+ only (hidden on smaller viewports
          inside the component). Editorial counterweight to the H1. */}
      <HeroApiSnippet />

      <div className="hero-container relative w-full max-w-[1240px] mx-auto px-6 lg:px-10">
        {/* Top padding scales with viewport: tight on laptops/phones so
            the validator + CTA fit above the fold; generous on 23"+
            externals where there's vertical room to breathe. */}
        <div className="pt-8 sm:pt-4 pb-12 sm:pb-16 md:pb-24 lg:pt-6 lg:pb-28 2xl:pt-16">
          <div className="flex flex-col items-start text-left">

            {/* Availability pill — intentionally no cleaned-volume
                counter until we have a public metric worth anchoring
                the page on. */}
            <div
              className="inline-flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full mb-6 sm:mb-7 relative whitespace-nowrap"
              style={{
                border: "1px solid rgba(15,81,50,0.25)",
                background: "#FFFFFF",
                boxShadow:
                  "0 1px 0 rgba(255,255,255,0.6) inset, 0 6px 20px -10px rgba(15,81,50,0.18)",
                fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                maxWidth: "100%",
              }}
              aria-label="Email cleaner online — free preview available"
            >
              <span className="relative inline-flex flex-shrink-0" style={{ width: 8, height: 8 }}>
                <span
                  aria-hidden
                  className="absolute inset-0 rounded-full"
                  style={{
                    background: "#0F5132",
                    animation: "hero-ring-pulse 1.8s cubic-bezier(.2,.8,.2,1) infinite",
                  }}
                />
                <span
                  className="relative rounded-full"
                  style={{
                    width: 8, height: 8,
                    background: "#0F5132",
                    boxShadow: "0 0 8px rgba(15,81,50,0.5)",
                  }}
                />
              </span>
              <span
                className="tabular-nums"
                style={{ fontSize: 11.5, fontWeight: 700, color: "#0F5132", letterSpacing: "0.04em" }}
              >
                Cleaner online
              </span>
              <span
                className="hidden sm:inline"
                style={{ fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase", color: "rgba(15,13,10,0.62)" }}
              >
                free preview · no signup
              </span>
              <span
                className="sm:hidden"
                style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: "rgba(15,13,10,0.62)" }}
              >
                free preview
              </span>
            </div>

            {/* Headline — word-by-word blur-rise reveal. Two-word
                stagger on the category line, italic emerald accent on
                line 2 with a slow shimmer sweep that loops every ~5s. */}
            <h1
              className="font-extrabold tracking-[-0.045em] leading-[0.95] sm:leading-[0.92]"
              style={{
                fontSize: "clamp(1.85rem, 7.6vw, 5.2rem)",
                color: "#0A0A0A",
              }}
            >
              {[
                { t: "Email deliverability", d: 0,  nowrap: true  },
                { t: "infrastructure",       d: 90, nowrap: false },
              ].map((w, i) => (
                <span
                  key={i}
                  className={`inline-block ${w.nowrap ? "hero-h1-nowrap" : ""}`}
                  style={{
                    marginRight: "0.28em",
                    whiteSpace: w.nowrap ? "nowrap" : undefined,
                    animation: `hero-word-rise 680ms cubic-bezier(.2,.8,.2,1) ${w.d}ms both`,
                  }}
                >
                  {w.t}
                </span>
              ))}
              <br />
              <em
                className="hero-h1-nowrap inline-block"
                style={{
                  fontStyle: "italic",
                  fontWeight: 700,
                  whiteSpace: "nowrap",
                  backgroundImage:
                    "linear-gradient(110deg, #0F5132 0%, #0F5132 35%, #2A9D6E 50%, #0F5132 65%, #0F5132 100%)",
                  backgroundSize: "200% 100%",
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  color: "#0F5132",
                  animation:
                    "hero-word-rise 680ms cubic-bezier(.2,.8,.2,1) 220ms both, hero-accent-shimmer 5.5s ease-in-out 1.4s infinite",
                }}
              >
                for serious senders.
              </em>
            </h1>

            {/* Subhead — benefit-first, plain English. Most marketers
                don't know what MX means but they know what dead
                inboxes feel like. */}
            <p
              className="mt-5 sm:mt-6 max-w-[58ch] leading-[1.55]"
              style={{
                fontSize: "clamp(0.95rem, 1.3vw, 1.15rem)",
                color: "rgba(10,10,10,0.62)",
                animation: "hero-rise 700ms cubic-bezier(.2,.8,.2,1) 120ms both",
              }}
            >
              Drop a CSV. Get back a{" "}
              <span style={{ color: "#0A0A0A", fontWeight: 600 }}>cleaner, import-ready list</span>{" "}
              — nine checks, live MX, catch-all review, every rejection audited. Then map its columns or
              compare it with another file without going back to a spreadsheet.
            </p>

            {/*
              ──────────────────────────────────────────────────────
              HEADLINE A/B TEST CANDIDATES (commented out)
              Keeping these in-source so swap-in is one block move.
              When tracking is wired (PostHog event: landing_viewed
              + conversion to /try), pick a variant, swap the H1
              block above for the alternate copy, and let it run for
              2-4 weeks before evaluating.

              VARIANT A — identity-based (current, Stripe-tier brand):
                "Email deliverability"
                "infrastructure"
                "for serious senders." (italic emerald accent)

              VARIANT B — outcome-driven (AIDA):
                "Send with fewer unknowns."
                "Every row explained."

              VARIANT C — pain-first (PAS):
                "Stop sending 100k emails to dead inboxes."

              VARIANT D — short imperative:
                "Validate before you send."

              VARIANT E — anti-competition / positioning (great for
              ads + comparison pages, may underperform on landing):
                "The deliverability layer"
                "your ESP doesn't ship." (italic emerald accent)

              Current = VARIANT A.
              ──────────────────────────────────────────────────────
            */}

            {/* Inline 1-email validator — full-width below subhead so
                the 6-chip verdict strip can breathe across the
                container. */}
            <div
              className="w-full"
              style={{ animation: "hero-rise 700ms cubic-bezier(.2,.8,.2,1) 240ms both" }}
            >
              <HeroLiveValidator />
            </div>

            {/* Primary CTAs */}
            <div
              className="flex flex-wrap items-center gap-3 mt-8"
              style={{ animation: "hero-rise 700ms cubic-bezier(.2,.8,.2,1) 360ms both" }}
            >
              <a
                href="/try"
                className="relative inline-flex items-center gap-2 transition-all hover:translate-x-[1px] overflow-hidden"
                style={{
                  background: "#0F5132",
                  color: "#FAF7EE",
                  padding: "0.9rem 1.4rem",
                  borderRadius: 10,
                  fontSize: 14,
                  fontWeight: 600,
                  boxShadow:
                    "0 18px 50px -14px rgba(15,81,50,0.30), 0 0 0 1px rgba(255,255,255,0.10) inset",
                }}
              >
                <span
                  aria-hidden
                  className="absolute inset-y-0 -left-1/4 w-1/3 pointer-events-none"
                  style={{
                    background:
                      "linear-gradient(110deg, transparent, rgba(255,255,255,0.35), transparent)",
                    animation: "hero-shimmer-x 4s ease-in-out infinite 0.8s",
                  }}
                />
                <span className="relative inline-flex items-center gap-2">
                  Clean my first list — free
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden>
                    <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
              </a>
              <a
                href="#how-it-works"
                className="inline-flex items-center gap-2 transition-colors hover:text-black"
                style={{ padding: "0.9rem 0.5rem", fontSize: 14, fontWeight: 500, color: "rgba(10,10,10,0.62)" }}
              >
                See it in action
                <svg width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden>
                  <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </a>
              {/* Hidden on mobile — wraps awkwardly under the CTAs and
                  the same reassurance lives on /pricing anyway. */}
              {/* Reassurance line. Color bumped from 0.42 -> 0.55 so it
                  passes WCAG AA (4.5:1) on the cream surface — at 0.42
                  it sat at ~2.8:1, failing for normal text. */}
              <span
                className="hidden sm:inline-flex items-center gap-2"
                style={{ fontSize: 12.5, color: "rgba(10,10,10,0.55)", marginLeft: 6 }}
              >
                <span aria-hidden style={{ width: 4, height: 4, borderRadius: 999, background: "#0F5132" }} />
                1,000 rows / month free · No credit card · Decision in ~420ms
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
